/**
 * MONSTER TOWN — THE STAGE AN INTERIOR STANDS ON, AS GENERATED ART.
 * OWNED BY THE INTERIORS CREW.
 *
 * Split out of `ambience.ts` to keep both files under the 500-line house rule.
 * Everything here is a canvas-2d texture built once and cached forever — zero
 * external assets, five textures for the whole game however many rooms exist.
 * `ambience.ts` owns WHERE these go and WHEN; this file owns what they look
 * like, which is the half that gets re-art-directed.
 *
 * THE ONE RULE ACROSS EVERYTHING IN HERE: the ROOM is the brightest, most
 * saturated thing in the frame and the stage is a clear value step below it, so
 * a kid's eye lands on the dollhouse and not on the floor around it. Round 3
 * enforced that rule with a COLD stage, which worked on contrast and failed on
 * mood — the interior came back reading like a debug viewport next to the sunny
 * town. Every surface here is now warm oak, wheat and terracotta at the SAME
 * value ladder. Temperature was never the mechanism; value is.
 */

import * as THREE from 'three'
import { mix, type PaletteName } from '../core/palette'

/** `mix()` returns a three colour number; canvas wants `#rrggbb`. */
function mixCss(a: PaletteName, b: PaletteName, t: number): string {
  return `#${mix(a, b, t).toString(16).padStart(6, '0')}`
}

// ---------------------------------------------------------------------------
// WHAT COLOUR IS THIS ROOM'S STAGE — DATA
// ---------------------------------------------------------------------------

/**
 * ============ EVERY INTERIOR USED TO STAND IN THE SAME LIVING ROOM ============
 *
 * VR1 BLOCKER, and it was two faults with one cause. The stage was ONE set of
 * baked textures shared by every interior in the game: an unbroken dark
 * wood-plank plane out to the edge of the frame, and a big round braided rug
 * with a red dotted border. So the kitchen, the bathroom, the ice cream shop,
 * the town shop AND VENUE #3 all stood on the same domestic floor covering, and
 * the critic's note is unanswerable — *"the zoo is standing on a living-room
 * rug"*, and *"it makes every interior in the game look like it is in the same
 * house"*.
 *
 * The metaphor is now a MODEL ON A DISPLAY TRAY, not a dollhouse on the carpet.
 * A tray is neutral: a kitchen can stand on one and so can a zoo. It is fitted
 * to the room instead of being a fixed multiple of it (see `BASE_MARGIN` in
 * ambience.ts), so "a rug bigger than the building" cannot happen again by
 * construction, and its colour comes from THIS TABLE, so two rooms are two
 * places.
 *
 * THE TABLE IS DATA and the lookup is a substring, exactly like the signposts'
 * `PLACE_ICON`: a venue added tomorrow adds one line, and one that does not adds
 * nothing — `stageHue` falls back to a stable hash over the pastel ladder, so an
 * unknown interior still gets its own colour rather than the first one.
 */
const STAGE_HUE: readonly (readonly [string, PaletteName])[] = [
  ['zoo', 'grassLight'],
  ['kitchen', 'wallMint'],
  ['bath', 'wallBlue'],
  ['bed', 'wallLilac'],
  ['living', 'wallCream'],
  // `house` is the living room's own scene id and must be tested AFTER the
  // rooms inside it, or `house_kitchen` would take the living room's colour.
  ['house', 'wallCream'],
  ['ice', 'wallPink'],
  ['restaurant', 'roofMustard'],
  ['shop', 'sand'],
]
const STAGE_LADDER: readonly PaletteName[] = ['wallCream', 'wallMint', 'wallBlue', 'wallPink', 'wallLilac']

function stageHue(sceneId: string): PaletteName {
  const low = sceneId.toLowerCase()
  for (const [frag, name] of STAGE_HUE) {
    if (low.includes(frag)) return name
  }
  let h = 0
  for (let i = 0; i < low.length; i++) h = (h * 31 + low.charCodeAt(i)) >>> 0
  return STAGE_LADDER[h % STAGE_LADDER.length] ?? 'wallCream'
}

/**
 * THE VALUE LADDER IS THE ONE RULE THAT SURVIVED FROM ROUND 3, because it was
 * always the right rule and only the materials were wrong:
 *
 *   surround  <  tray  <  room floor  <  plaster
 *
 * The eye still walks inward to the room. What changed is that every rung is now
 * derived from the room's OWN hue, and the textures below are drawn in VALUES
 * (near-white down) so one texture multiplied by one colour gives every interior
 * its own stage for the same three draw calls and no extra canvases.
 */
export interface StageLook {
  /** The surround the tray stands on. */
  readonly ground: number
  /** The tray itself. */
  readonly base: number
  /** The tray's hard rim, and the seams in the surround. */
  readonly rim: number
  /**
   * WHERE THE FLOOR STOPS BEING A FLOOR. The colour the surround dissolves into
   * on its way to the frame edge, and the same colour the backdrop wall meets it
   * with, so the two make one horizon instead of two surfaces. See `hazeTexture`
   * and the `haze` quad in ambience.ts.
   */
  readonly far: number
  /** Cache key for the backdrop, which cannot be tinted by a material. */
  readonly key: PaletteName
}

/**
 * ===========================================================================
 *  THE COMMENT AT THE TOP OF THIS FILE SAID WARM AND THE CODE SAID `inkSoft`
 * ===========================================================================
 * VR2 BLOCKER, and it is one line's worth of cause. `inkSoft` is `#4A3E58` — a
 * cold plum, the darkest cool value in the palette — and every rung of the stage
 * was mixed toward it. For the kitchen (`wallMint`) that lands the surround on
 * `#97A5A2`: **the coldest colour anywhere in the game, filling 40-60 % of the
 * frame, in the two headline new places of this expansion.** The critic read it
 * as *"the same grey as a transparency checkerboard"* and as an unfinished
 * editor view, and that is a fair reading of a cold neutral grid.
 *
 * The rule this file states — "warm oak, wheat and terracotta at the SAME value
 * ladder; temperature was never the mechanism, value is" — is right and was
 * simply never implemented. Every rung now mixes toward `dirtDark`/`trunkDark`,
 * which are the palette's warm darks, and the ladder is unchanged:
 *
 *   far 118  <  ground 174  <  tray 212  <  room floor ~230   (kitchen, luma)
 *
 * so the eye still walks inward to the room — it just walks across a warm floor
 * now instead of a cold one.
 */
export function stageLook(sceneId: string): StageLook {
  const hue = stageHue(sceneId)
  return {
    ground: mix(hue, 'dirtDark', 0.45),
    base: mix(hue, 'pathEdge', 0.3),
    rim: mix(hue, 'dirtDark', 0.62),
    far: mix(hue, 'trunkDark', 0.72),
    key: hue,
  }
}

// ---------------------------------------------------------------------------
// generated textures (zero external assets)
// ---------------------------------------------------------------------------

let floorTex: THREE.Texture | null = null
let baseTex: THREE.Texture | null = null
let vignetteTex: THREE.Texture | null = null
let shadowTex: THREE.Texture | null = null
const skyTex = new Map<PaletteName, THREE.Texture>()

function canvasTex(size: number, draw: (c: CanvasRenderingContext2D, s: number) => void): THREE.Texture {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const c = canvas.getContext('2d')
  if (c === null) throw new Error('interior ambience: no 2d context')
  draw(c, size)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 1
  return tex
}

/**
 * THE SURROUND THE TRAY STANDS ON — drawn in VALUES, tinted by the material.
 *
 * THE CONSTRAINT FROM ROUND 3 IS UNCHANGED AND IS WHY THIS IS NOT A GRADIENT: a
 * pattern with HARD EDGES, tiled small enough that it is always MINIFIED rather
 * than magnified. At interior framing one 256-pixel repeat covers six world
 * units — the texture is being shrunk, so there is nothing for a magnifier to
 * smear. Round 1's blurry brown plane and round 2's edgeless mauve ramp both
 * died on that constraint and it still stands.
 *
 * WHAT CHANGED (vr1): the pattern was FLOORBOARDS, and floorboards are the
 * grammar of a house. Under a zoo they are a lie, and shared by all seven
 * interiors they are the reason "every interior in the game looks like it is in
 * the same house". Boards are gone; what is left is the quietest hard-edged
 * surface that can still hold an edge — a wide seam grid with a lit lip, and a
 * sparse motif so it is obviously drawn. It says "the surface the model is
 * standing on" and nothing else.
 *
 * NEAR-WHITE, DELIBERATELY. A `map` MULTIPLIES the material colour, so drawing
 * this in values from 1.0 down lets ONE texture be every room's surround: the
 * colour comes from `StageLook.ground` on the material. Seven interiors, one
 * canvas, one draw call.
 */
export function floorTexture(): THREE.Texture {
  if (floorTex !== null) return floorTex
  const t = canvasTex(256, (c, s) => {
    const grey = (v: number): string => {
      const n = Math.round(255 * v)
      return `rgb(${n},${n},${n})`
    }
    /*
     * ============ AND IT MAY NOT READ AS A CHECKERBOARD (fixed vr2) ==========
     * The seam ran at 0.78 against a 0.93 face — a 15 % value step, in a regular
     * grid, repeated to the edge of the frame. That is a checkerboard however
     * warm you tint it, and it is half of why two interiors read as an editor
     * viewport. The hard edge stays (round 3's constraint is still right: this
     * surface is always MINIFIED, so a soft one would smear), but it is now a
     * quiet grain rather than a pattern with a period a kid can count.
     */
    // The panel face sits a little below full, so the lit lip below each seam
    // has somewhere brighter to go. 1.0 is the brightest this surface may be.
    c.fillStyle = grey(0.97)
    c.fillRect(0, 0, s, s)
    // Two big panels per repeat: the seam is the hard edge that keeps this
    // legible when it is minified, and two is few enough never to read as tiling.
    const half = s / 2
    const sw = Math.max(1, s / 128)
    c.fillStyle = grey(0.88)
    for (let i = 0; i < 2; i++) {
      c.fillRect(0, i * half, s, sw)
      c.fillRect(i * half, 0, sw, s)
    }
    // The lit lip just past each seam. Light comes from above, so the near side
    // of every panel catches it — this is what gives the surface thickness.
    c.fillStyle = grey(1)
    for (let i = 0; i < 2; i++) {
      c.fillRect(0, i * half + sw, s, sw)
      c.fillRect(i * half + sw, 0, sw, s)
    }
    // A sparse motif on each panel: four small diamonds, low contrast. Hard
    // corners, so a blur can never have them, and quiet enough that the ROOM is
    // still the thing the eye lands on.
    c.fillStyle = grey(0.93)
    for (let gy = 0; gy < 2; gy++) {
      for (let gx = 0; gx < 2; gx++) {
        const px = gx * half + half / 2
        const py = gy * half + half / 2
        const r = s * 0.028
        c.beginPath()
        c.moveTo(px, py - r)
        c.lineTo(px + r * 0.7, py)
        c.lineTo(px, py + r)
        c.lineTo(px - r * 0.7, py)
        c.closePath()
        c.fill()
      }
    }
  })
  t.wrapS = THREE.RepeatWrapping
  t.wrapT = THREE.RepeatWrapping
  // The one surface in an interior sampled at a grazing 34-degree pitch.
  t.anisotropy = 4
  floorTex = t
  return t
}

/**
 * THE TRAY THE ROOM STANDS ON. A fitted rounded rectangle, not a rug.
 *
 * WHAT IT REPLACES (vr1 blocker): a round braided rug with a terracotta dotted
 * border, drawn as a SQUARE scaled by `max(width, depth) * 1.72` — so on any
 * room that is not square it reached "well OUTSIDE the room's own walls on all
 * four sides", and the critic's reading of that is exactly right: *"a rug bigger
 * than the building reads as a bug, not a style"*. It is now sized to the room's
 * two axes separately with a fixed margin (ambience.ts, `BASE_MARGIN`), so the
 * lip around the plinth is the same width on a 6x6 bathroom and a 20-unit venue
 * and can never grow into a carpet.
 *
 * RECTANGULAR, because the thing standing on it is. A round rug under a
 * rectangular room leaves four corners of nothing, which is what put so much
 * peach in the frame.
 *
 * Drawn in VALUES like the surround, and tinted per room by the material.
 */
export function baseTexture(): THREE.Texture {
  if (baseTex !== null) return baseTex
  const t = canvasTex(512, (c, s) => {
    c.clearRect(0, 0, s, s)
    const grey = (v: number): string => {
      const n = Math.round(255 * v)
      return `rgb(${n},${n},${n})`
    }
    const rr = (inset: number, radius: number): void => {
      const x = inset * s
      const w = s - inset * s * 2
      const r = radius * s
      c.beginPath()
      c.moveTo(x + r, x)
      c.arcTo(x + w, x, x + w, x + w, r)
      c.arcTo(x + w, x + w, x, x + w, r)
      c.arcTo(x, x + w, x, x, r)
      c.arcTo(x, x, x + w, x, r)
      c.closePath()
    }
    // The rim: one hard border, one value, no braid and no dots. It is a VALUE
    // step off the same tint as the face, so a tray is one object in one colour
    // rather than a rug in three.
    rr(0.012, 0.055)
    c.fillStyle = grey(0.62)
    c.fill()
    // The tray face.
    rr(0.032, 0.045)
    c.fillStyle = grey(1)
    c.fill()
    // An inner scribe line a finger's width in, the way a real tray has one.
    // Hard edge, low contrast: the detail that says OBJECT rather than falloff.
    rr(0.075, 0.035)
    c.strokeStyle = grey(0.88)
    c.lineWidth = Math.max(1, s / 170)
    c.stroke()
    // ...and a soft inner shading against the rim, so the tray has a lip rather
    // than being a flat sticker. Four bands, hard-edged, cheap.
    for (let i = 0; i < 3; i++) {
      rr(0.036 + i * 0.006, 0.045)
      c.strokeStyle = grey(0.9 + i * 0.03)
      c.lineWidth = Math.max(1, s / 190)
      c.stroke()
    }
  })
  // NOT repeating: one tray, one image, bounded.
  t.wrapS = THREE.ClampToEdgeWrapping
  t.wrapT = THREE.ClampToEdgeWrapping
  baseTex = t
  return t
}

/**
 * ===========================================================================
 *  THE FLOOR HAD NO END, SO THE WALL BEHIND IT WAS NEVER ONCE SEEN (vr2)
 * ===========================================================================
 * `interiorSkyTexture` below has been building "the part of the frame above the
 * surround" — a wall in shade, a warm wash, a picture rail, a dado — since vr1.
 * Nobody has ever seen it. The surround is a 220-unit plane at y = −0.44 under
 * a camera pitched about 34 degrees, so it rises behind the room and fills the
 * frame right past the roofline: there IS no part of the frame above the
 * surround. What a kid sees instead is *"an infinite tiled plane"* with the
 * room's own walls sticking up out of it — which is the whole of the critic's
 * *"a tan pad on a green mat on a grey grid"*, and it is why the interiors read
 * as an unfinished editor view beside `shots/critic-vr2/wide-1200.png`.
 *
 * This is the horizon. A bounded quad, sized to the ROOM (see `haze` in
 * ambience.ts), transparent over the room and its tray and ramping to
 * `StageLook.far` at its rim — the SAME colour the backdrop meets it with, so
 * the floor dissolves into the wall in one move instead of ending at a line.
 * One extra draw call, alpha only, no lighting, and nothing is magnified: a
 * falloff is a falloff at any size, which is exactly why the round-1 "blurry
 * plane" objection does not apply to this one.
 *
 * Sized to the room rather than to a constant is the lesson from the vignette
 * two functions down: a ring drawn 55 world units out is nowhere near anything.
 */
let hazeTex: THREE.Texture | null = null
export function hazeTexture(): THREE.Texture {
  if (hazeTex !== null) return hazeTex
  const t = canvasTex(256, (c, s) => {
    c.clearRect(0, 0, s, s)
    // Drawn in WHITE with an alpha ramp; the material carries `StageLook.far`.
    const g = c.createRadialGradient(s / 2, s / 2, s * 0.16, s / 2, s / 2, s * 0.5)
    g.addColorStop(0, 'rgba(255,255,255,0)')
    g.addColorStop(0.42, 'rgba(255,255,255,0)')
    g.addColorStop(0.62, 'rgba(255,255,255,0.42)')
    g.addColorStop(0.82, 'rgba(255,255,255,0.88)')
    g.addColorStop(1, 'rgba(255,255,255,1)')
    c.fillStyle = g
    c.fillRect(0, 0, s, s)
  })
  t.wrapS = THREE.ClampToEdgeWrapping
  t.wrapT = THREE.ClampToEdgeWrapping
  hazeTex = t
  return t
}

/**
 * The vignette. A warm sunlit haze around the room, closing to a soft amber
 * dusk at the edge of the frame. This is what turns "a box in the void" into
 * "a pool of light on a floor".
 *
 * TWO RAMPS, DRAWN IN ORDER, both centred: a low-alpha warm cream lift over the
 * middle third (the light an implied window throws across the playroom floor
 * around the dollhouse) and then the falloff. Centred is not a stylistic choice
 * — this quad is a child of the CAMERA and the camera turns in 90-degree snaps,
 * so anything directional baked in here would swing across the frame every time
 * a kid taps rotate. A centred pool is the only light that can hold still.
 *
 * The falloff is deliberately still LIGHT (round-2 lesson: the stage under the
 * room carries its own falloff, and at the old strengths the two stacked and the
 * frame went muddy) and the colour is a warm brown rather than the cold plum ink
 * it used to be — cold corners were half of why the whole screen read chilly.
 */
export function vignetteTexture(): THREE.Texture {
  if (vignetteTex !== null) return vignetteTex
  const t = canvasTex(256, (c, s) => {
    c.clearRect(0, 0, s, s)
    // Radius 0.5 is the middle of an edge; 0.707 is a corner.
    const sun = c.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s * 0.5)
    sun.addColorStop(0, 'rgba(255,240,206,0.09)')
    sun.addColorStop(0.42, 'rgba(255,238,200,0.06)')
    sun.addColorStop(0.72, 'rgba(255,236,196,0.0)')
    sun.addColorStop(1, 'rgba(255,236,196,0.0)')
    c.fillStyle = sun
    c.fillRect(0, 0, s, s)

    /*
     * DEEPER THAN IT WAS (vr2). At 0.22 in the corners this was doing almost
     * nothing against a plane that fills half the frame, and "the frame is never
     * empty" was being carried entirely by the surround's own colour. The
     * horizon quad above does the work now; this closes the corners on top of
     * it, warm, so the picture ends instead of running out.
     */
    const g = c.createRadialGradient(s / 2, s / 2, s * 0.11, s / 2, s / 2, s * 0.5)
    g.addColorStop(0, 'rgba(74,50,36,0.0)')
    g.addColorStop(0.52, 'rgba(74,50,36,0.0)')
    g.addColorStop(0.78, 'rgba(74,50,36,0.16)')
    g.addColorStop(1, 'rgba(58,38,28,0.4)')
    c.fillStyle = g
    c.fillRect(0, 0, s, s)
  })
  t.wrapS = THREE.ClampToEdgeWrapping
  t.wrapT = THREE.ClampToEdgeWrapping
  vignetteTex = t
  return t
}

/**
 * A soft rounded blob: the room's own shadow on the tray. Warm brown, not cold
 * plum — a shadow cast by sunlight onto a warm surface is that surface's own
 * colour with the light taken out of it, and a violet one under a cream house
 * was reading as a bruise rather than as contact.
 */
export function shadowTexture(): THREE.Texture {
  if (shadowTex !== null) return shadowTex
  const t = canvasTex(128, (c, s) => {
    const g = c.createRadialGradient(s / 2, s / 2, s * 0.05, s / 2, s / 2, s * 0.5)
    g.addColorStop(0, 'rgba(88,58,40,0.42)')
    g.addColorStop(0.52, 'rgba(88,58,40,0.28)')
    g.addColorStop(0.78, 'rgba(88,58,40,0.10)')
    g.addColorStop(1, 'rgba(88,58,40,0.0)')
    c.clearRect(0, 0, s, s)
    c.fillStyle = g
    c.fillRect(0, 0, s, s)
  })
  t.wrapS = THREE.ClampToEdgeWrapping
  t.wrapT = THREE.ClampToEdgeWrapping
  shadowTex = t
  return t
}

/**
 * THE WALL BEHIND THE STAGE — the part of the frame above the surround.
 *
 * A `scene.background` texture is stretched to the CANVAS, so anything drawn in
 * it that is not a horizontal band changes shape when the iPad is turned. Bands
 * are therefore the whole vocabulary here, and that is enough to build a room:
 * a wall in shade near the ceiling, a warm glow where the light washes down it,
 * a picture rail, and a darker dado meeting the surround.
 *
 * ONE PER ROOM HUE (vr1). A background cannot be tinted the way a mapped
 * material can — `scene.background` has no colour multiplier — so this is the
 * one thing on the stage that has to be baked per hue rather than tinted. It is
 * cached by hue and there are five hues in the ladder, so the whole game pays at
 * most five 256px canvases, once, and only for hues a kid actually walks into.
 */
export function interiorSkyTexture(look: StageLook): THREE.Texture {
  const cached = skyTex.get(look.key)
  if (cached !== undefined) return cached
  const hue = look.key
  const t = canvasTex(256, (c, s) => {
    /*
     * WARM, AND IT MEETS THE FLOOR (vr2). Same one-line cause as `stageLook`:
     * every stop was mixed toward the cold plum `inkSoft`. The bottom stop is
     * now exactly `StageLook.far`, which is the colour the horizon quad ramps
     * to, so wall and floor become one continuous sweep rather than two
     * surfaces meeting at an edge.
     */
    const g = c.createLinearGradient(0, 0, 0, s)
    g.addColorStop(0, mixCss(hue, 'trunkDark', 0.62))
    g.addColorStop(0.34, mixCss(hue, 'trunkDark', 0.44))
    // the wash of the light on the far wall
    g.addColorStop(0.56, mixCss(hue, 'sand', 0.34))
    g.addColorStop(0.74, mixCss(hue, 'trunkDark', 0.5))
    g.addColorStop(1, mixCss(hue, 'trunkDark', 0.72))
    c.fillStyle = g
    c.fillRect(0, 0, s, s)
    // A picture rail: one crisp horizontal line and its shadow. Cheap, and the
    // single strongest cue that this is a wall rather than a gradient.
    c.fillStyle = mixCss('sand', 'sunWarm', 0.5)
    c.fillRect(0, Math.round(s * 0.505), s, Math.max(1, s / 128))
    c.fillStyle = mixCss(hue, 'trunkDark', 0.8)
    c.fillRect(0, Math.round(s * 0.505) + Math.max(1, s / 128), s, Math.max(1, s / 110))
  })
  t.wrapS = THREE.ClampToEdgeWrapping
  t.wrapT = THREE.ClampToEdgeWrapping
  skyTex.set(look.key, t)
  return t
}
